import { useEffect, useState } from "react";
import { Sparkles, X, Crown } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const SEEN_KEY = "studict_freetrial_dismissed_at";
const SEEN_TTL_HOURS = 24; // re-show next day if dismissed

function recentlyDismissed() {
  try {
    const t = Number(localStorage.getItem(SEEN_KEY) || 0);
    return t > 0 && (Date.now() - t) / 3600000 < SEEN_TTL_HOURS;
  } catch { return false; }
}

export default function FreeTrialPopup() {
  const { user, checkAuth } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (recentlyDismissed()) return;
    api.get("/free-trial/config").then((r) => {
      if (r.data?.show) {
        setCfg(r.data);
        setShow(true);
      }
    }).catch(() => { /* silent */ });
  }, [user]);

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch { /* ignore */ }
    setShow(false);
  };

  const claim = async () => {
    setBusy(true);
    try {
      const r = await api.post("/free-trial/claim");
      toast.success(r.data.message || "🎉 Trial activated!", { duration: 6000 });
      await checkAuth();
      setShow(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial");
    } finally { setBusy(false); }
  };

  if (!show || !cfg) return null;

  const planLabel = cfg.plan === "pro_plus" ? "Pro Plus" : "Pro";

  return (
    <div
      data-testid="free-trial-popup"
      className="fixed inset-0 z-50 bg-slate-900/60 grid place-items-center p-4 backdrop-blur-sm"
      onClick={dismiss}
    >
      <div
        className="w-full max-w-md bg-white border-2 border-slate-900 rounded-2xl p-6 sm:p-8"
        style={{ boxShadow: "8px 8px 0 0 #0F172A" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <span className="inline-flex items-center gap-1 nb-tag bg-yellow-300 text-xs">
            <Sparkles className="w-3 h-3" strokeWidth={2.5}/> Limited offer
          </span>
          <button
            onClick={dismiss}
            aria-label="Close"
            data-testid="free-trial-close"
            className="w-8 h-8 grid place-items-center bg-white border-2 border-slate-900 rounded-md"
          >
            <X className="w-4 h-4" strokeWidth={2.5}/>
          </button>
        </div>
        <h2 className="font-display text-3xl font-black mt-4 leading-tight flex items-center gap-2">
          <Crown className="w-7 h-7" strokeWidth={2.5}/> {cfg.headline}
        </h2>
        <p className="text-sm font-medium text-slate-700 mt-3">{cfg.subhead}</p>
        <div className="mt-5 p-4 border-2 border-slate-900 rounded-lg bg-emerald-100">
          <div className="text-xs font-bold uppercase tracking-widest">You'll get</div>
          <div className="font-display text-2xl font-black mt-1">{planLabel} · {cfg.days} day{cfg.days !== 1 ? "s" : ""}</div>
          <div className="text-xs text-slate-700 mt-1">No card needed. No auto-charge.</div>
        </div>
        <button
          data-testid="free-trial-claim"
          onClick={claim}
          disabled={busy}
          className="nb-btn-primary mt-5 w-full inline-flex items-center justify-center gap-2"
        >
          <Sparkles className="w-4 h-4" strokeWidth={2.5}/> {busy ? "Activating…" : "Start free trial"}
        </button>
        <button
          onClick={dismiss}
          data-testid="free-trial-dismiss"
          className="text-xs font-bold text-slate-500 mt-3 w-full text-center hover:underline"
        >Maybe later</button>
      </div>
    </div>
  );
}
